import { useState } from 'react';
import type { MealPlanDto, MealSlot } from '@fewerbags/schema';
import { formatDayLong } from '../../lib/planDates';
import { SLOT_LABEL, SLOT_OPTIONS } from '../../lib/plans';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { SelectField } from '../SelectField';

/**
 * Touch-friendly fallback for drag-and-drop on the week grid: move a meal to
 * another day/slot by picking them explicitly, rather than dragging.
 */
export function MovePlanDialog({
  open,
  onClose,
  plan,
  busy,
  error,
  onMove,
}: {
  open: boolean;
  onClose: () => void;
  plan: MealPlanDto;
  busy: boolean;
  error?: string | undefined;
  onMove: (date: string, slot: MealSlot) => void;
}) {
  const [date, setDate] = useState(plan.date);
  const [slot, setSlot] = useState<MealSlot>(plan.slot);

  return (
    <Dialog open={open} onClose={onClose} title={`Move ${formatDayLong(plan.date)}'s meal`}>
      <p className="text-sm text-muted">
        Currently {SLOT_LABEL[plan.slot]} on {formatDayLong(plan.date)}.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label htmlFor="move-plan-date" className="text-sm font-medium text-ink">
            Date
          </label>
          <input
            id="move-plan-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink"
          />
        </div>
        <SelectField
          id="move-plan-slot"
          label="Meal"
          value={slot}
          onChange={(e) => setSlot(e.target.value as MealSlot)}
          options={SLOT_OPTIONS}
        />
      </div>
      {error ? <Alert tone="error">{error}</Alert> : null}
      <div className="flex gap-2">
        <Button
          disabled={busy || (date === plan.date && slot === plan.slot)}
          onClick={() => onMove(date, slot)}
        >
          {busy ? 'Moving…' : 'Move'}
        </Button>
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
      </div>
    </Dialog>
  );
}
